import { useRef, useState } from 'react';
import { api, qs } from '../lib/api.js';
import { useAction, useResource } from '../lib/useApi.js';
import type { Design, DirectoryUser, ImportResponse } from '../lib/types.js';
import { Banner, bytes, ErrorState, Field, Loading, Modal, Pill } from './ui.js';
import { PreviewGrid } from './DevicePreview.js';
import { LintPanel, LintSummary } from './LintPanel.js';

type Source = 'html' | 'design';

/**
 * Import a signature into the editor.
 *
 * Two-step by design: parse and show what happened, then let the reviewer accept
 * it. An import is a translation with judgement calls in it -- which cell was a
 * divider, which paragraph was the disclaimer, whose name became a merge field --
 * so applying it silently would be the wrong shape. Nothing is saved here
 * either: accepting loads the design into the editor's working copy, and it
 * still goes through the normal save-and-review path.
 */
export function ImportModal({
  onClose,
  onApply,
}: {
  onClose: () => void;
  onApply: (design: Design, summary: string) => void;
}): JSX.Element {
  const [source, setSource] = useState<Source>('html');
  const [html, setHtml] = useState('');
  const [designText, setDesignText] = useState('');
  const [tokeniseUpn, setTokeniseUpn] = useState('');
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const directory = useResource<{ users: DirectoryUser[] }>(
    `/api/directory/users${qs({ take: 200 })}`,
  );

  const run = useAction(async () => {
    setResult(null);
    if (source === 'html') {
      const res = await api.post<ImportResponse>('/api/import/html', {
        html,
        ...(tokeniseUpn ? { tokeniseUpn } : {}),
      });
      setResult(res);
    } else {
      const res = await api.post<ImportResponse>('/api/import/design', { content: designText });
      setResult(res);
    }
  });

  const pickFile = async (file: File) => {
    const text = await file.text();
    setFileName(file.name);
    setResult(null);
    if (/\.json$/i.test(file.name)) {
      setSource('design');
      setDesignText(text);
    } else {
      setSource('html');
      setHtml(text);
    }
  };

  const input = source === 'html' ? html : designText;
  const canParse = input.trim().length > 0;
  const blockingErrors = result?.lint.counts.error ?? 0;
  const errors = (result?.lint.findings ?? []).filter((f) => f.severity === 'error');
  const hasFindings = (result?.lint.findings.length ?? 0) > 0;

  return (
    <Modal
      title="Import a signature"
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          {!result && (
            <button className="primary" disabled={!canParse || run.pending} onClick={() => void run.run()}>
              {run.pending ? 'Reading…' : 'Read it'}
            </button>
          )}
          {result && (
            <>
              <button onClick={() => setResult(null)}>Back</button>
              <button
                className="primary"
                onClick={() =>
                  onApply(
                    result.design,
                    source === 'html'
                      ? `Imported from HTML (${result.stats.blocks} blocks, ${result.stats.tokenised} merge fields)`
                      : 'Imported from a design file',
                  )
                }
              >
                Use this design
              </button>
            </>
          )}
        </>
      }
    >
      {!result && (
        <>
          <div className="seg mb">
            <button className={source === 'html' ? 'on' : ''} onClick={() => setSource('html')}>
              Existing signature HTML
            </button>
            <button className={source === 'design' ? 'on' : ''} onClick={() => setSource('design')}>
              Exported design file
            </button>
          </div>

          {run.error && <ErrorState error={run.error} />}

          {source === 'html' ? (
            <>
              <p className="tiny muted">
                Paste the HTML of a signature you already use. In Outlook, the reliable source is
                the <code>.htm</code> file in
                {' '}
                <code>%APPDATA%\Microsoft\Signatures</code> — copying from the compose window loses
                the markup. Word artefacts, conditional comments and nested layout tables are
                handled.
              </p>

              <Field
                label="Whose signature is this?"
                hint="Their name, job title, phone and email are turned back into merge fields, which is what makes it a template rather than one person's signature. Leave blank to import literally."
              >
                <select value={tokeniseUpn} onChange={(e) => setTokeniseUpn(e.target.value)}>
                  <option value="">Import literally (no merge fields)</option>
                  {(directory.data?.users ?? []).map((u) => (
                    <option key={u.upn} value={u.upn}>
                      {u.displayName ?? u.upn}
                      {u.jobTitle ? ` — ${u.jobTitle}` : ''}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Signature HTML">
                <textarea
                  rows={10}
                  className="mono"
                  value={html}
                  placeholder={'<table>\n  <tr><td>…</td></tr>\n</table>'}
                  onChange={(e) => {
                    setHtml(e.target.value);
                    setFileName(null);
                  }}
                />
              </Field>
            </>
          ) : (
            <>
              <p className="tiny muted">
                Paste a design file exported from this tool, from another environment or another
                template. Approval state is never imported, so it arrives as a draft and still needs
                review.
              </p>
              <Field label="Design JSON">
                <textarea
                  rows={10}
                  className="mono"
                  value={designText}
                  placeholder='{ "kind": "email-sig-manager/design", … }'
                  onChange={(e) => {
                    setDesignText(e.target.value);
                    setFileName(null);
                  }}
                />
              </Field>
            </>
          )}

          <div className="row">
            <input
              ref={fileInput}
              type="file"
              accept=".htm,.html,.json,.txt"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void pickFile(file);
              }}
            />
            <button onClick={() => fileInput.current?.click()}>Choose a file instead</button>
            {fileName && (
              <span className="tiny muted">
                {fileName} · {bytes(new Blob([input]).size)}
              </span>
            )}
            <span className="spacer" />
            {canParse && <span className="tiny muted">{bytes(new Blob([input]).size)} to read</span>}
          </div>
        </>
      )}

      {run.pending && <Loading rows={3} label="Reading the signature" />}

      {result && (
        <>
          <div className="row wrap mb">
            <Pill tone="accent">{result.stats.blocks} blocks</Pill>
            {result.stats.images > 0 && <Pill tone="outline">{result.stats.images} images</Pill>}
            {result.stats.tokenised > 0 && (
              <Pill tone="ok">
                {result.stats.tokenised} merge field{result.stats.tokenised === 1 ? '' : 's'}
                {result.stats.tokenisedFields?.length
                  ? `: ${result.stats.tokenisedFields.join(', ')}`
                  : ''}
              </Pill>
            )}
            {result.stats.tablesFlattened > 0 && (
              <Pill tone="outline">{result.stats.tablesFlattened} tables flattened</Pill>
            )}
            <span className="spacer" />
            <LintSummary lint={result.lint} />
          </div>

          {blockingErrors > 0 && (
            <Banner tone="danger">
              <strong>
                {blockingErrors} blocking issue{blockingErrors === 1 ? '' : 's'} — must be fixed
                before this can go for review
              </strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {errors.map((finding, i) => (
                  <li key={i} className="tiny">
                    {finding.message}
                    {finding.hint && <div className="muted">{finding.hint}</div>}
                  </li>
                ))}
              </ul>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                You can still accept the import and fix these in the editor.
              </div>
            </Banner>
          )}

          {result.stats.tokenised === 0 && (
            <Banner tone="warn">
              <strong>No merge fields</strong>
              <div className="tiny">
                Every recipient would get the details exactly as pasted. Go back and pick whose
                signature this is, or replace the personal details by hand after importing.
              </div>
            </Banner>
          )}

          {result.notes.length > 0 && (
            <>
              <div className="field-label">What the importer did</div>
              <ul style={{ margin: '0 0 14px', paddingLeft: 18 }}>
                {result.notes.map((note, i) => (
                  <li
                    key={i}
                    className="tiny"
                    style={{ color: note.level === 'warning' ? 'var(--warn)' : 'var(--text-2)' }}
                  >
                    {note.message}
                  </li>
                ))}
              </ul>
            </>
          )}

          {result.dropped.length > 0 && (
            <>
              <div className="field-label">Could not be imported ({result.dropped.length})</div>
              <pre className="log" style={{ maxHeight: 120 }}>
                {result.dropped.join('\n')}
              </pre>
            </>
          )}

          {hasFindings && (
            <>
              <div className="field-label" style={{ marginTop: 14 }}>
                Rendering issues in the imported design ({result.lint.findings.length})
              </div>
              <div
                className="card"
                style={{ marginBottom: 14, overflow: 'hidden' }}
              >
                <LintPanel lint={result.lint} />
              </div>
            </>
          )}

          {result.previews && result.previews.length > 0 && (
            <>
              <div className="field-label" style={{ marginTop: 14 }}>
                How it renders
              </div>
              <PreviewGrid previews={result.previews} />
            </>
          )}

          <Banner tone="info">
            Accepting this replaces the design you are editing. Nothing is saved until you save a
            new version.
          </Banner>
        </>
      )}
    </Modal>
  );
}
